import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, MapPin, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import Logotype from '@/components/shared/Logotype';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

function PasswordGate({ onAccess }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);

  const verify = async () => {
    try {
      const { data } = await axios.post(`${API}/public/framed/verify`, { password: pw });
      localStorage.setItem('framed_token', data.token);
      onAccess(data.token);
    } catch { setError(true); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6" data-testid="curated-password-gate">
      <div className="w-full max-w-xs text-center">
        <Lock size={20} className="mx-auto mb-6 text-[#b0b4c0]" />
        <h1 className="text-3xl mb-1" style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5' }}>Framed</h1>
        <p className="text-xs tracking-[0.15em] uppercase mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
          Curated Collection
        </p>
        {error && <p className="text-red-400 text-xs mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Wrong password</p>}
        <Input type="password" value={pw}
          onChange={e => { setPw(e.target.value); setError(false); }}
          onKeyDown={e => e.key === 'Enter' && verify()}
          className="bg-transparent border-0 border-b border-white/[0.06] rounded-none text-[#f0f0f5] text-center text-lg tracking-wider focus-visible:ring-0 focus-visible:border-[#b0b4c0]"
          data-testid="curated-password-input" autoFocus />
        <button onClick={verify} className="mt-8 text-xs tracking-[0.3em] uppercase hover:text-[#f0f0f5] transition-colors"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }} data-testid="curated-password-submit">
          Enter
        </button>
      </div>
    </div>
  );
}

function Viewer({ photos, index, onClose, onNavigate }) {
  const photo = photos[index];

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && index < photos.length - 1) onNavigate(index + 1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [index, photos.length, onClose, onNavigate]);

  if (!photo) return null;
  const tagline = photo.tagline || photo.title || photo.caption || '';
  const position = photo.tagline_position || 'below';

  return (
    <div className="fixed inset-0 bg-[#0a0a0f] z-[100] flex flex-col" data-testid="curated-viewer">
      <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 py-4">
        <span className="text-white/50 text-xs font-mono tracking-wider">{index + 1} / {photos.length}</span>
        <button onClick={onClose} className="text-white/70 hover:text-white transition-colors" data-testid="curated-viewer-close">
          <X size={24} />
        </button>
      </div>
      {index > 0 && (
        <button onClick={() => onNavigate(index - 1)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 text-white/20 hover:text-white/60 transition-colors">
          <ChevronLeft size={36} strokeWidth={1} />
        </button>
      )}
      {index < photos.length - 1 && (
        <button onClick={() => onNavigate(index + 1)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 text-white/20 hover:text-white/60 transition-colors">
          <ChevronRight size={36} strokeWidth={1} />
        </button>
      )}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        <img src={getImageUrl(photo.url)} alt="" className="max-w-full max-h-full object-contain" />
        {tagline && position === 'superimposed' && (
          <div className="absolute bottom-8 left-0 right-0 text-center px-8">
            <p className="text-white/80 text-sm md:text-base font-light italic" style={{ fontFamily: "'Cormorant Garamond', serif", textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}>
              {tagline}
            </p>
          </div>
        )}
      </div>
      <div className="px-6 md:px-12 py-4 text-center">
        {tagline && position === 'below' && (
          <p className="text-white/60 text-sm italic mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tagline}</p>
        )}
        <div className="flex items-center justify-center gap-4 text-white/40 text-xs tracking-wider">
          {photo.location && <span className="flex items-center gap-1"><MapPin size={10} />{photo.location}</span>}
          {photo.date_taken && <span>{photo.date_taken}</span>}
        </div>
      </div>
    </div>
  );
}

export default function CuratedGallery() {
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const [mode, setMode] = useState('loading');

  useEffect(() => {
    loadCollection();
  }, [collectionId]);

  const loadCollection = async () => {
    try {
      const { data } = await axios.get(`${API}/public/framed/curated/${collectionId}`);
      setCollection(data);
      setMode('gallery');
    } catch (err) {
      if (err.response?.status === 403) {
        setMode('private');
      } else {
        setMode('error');
      }
    }
  };

  const onNavigate = useCallback((i) => setViewerIndex(i), []);

  if (mode === 'loading') return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <ZLoadingBar accent="#D4537E" loading />
    </div>
  );

  if (mode === 'private') return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="text-center">
        <p className="text-[#6b7084] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>This collection is private</p>
        <Link to="/gallery" className="text-xs tracking-[0.2em] uppercase text-[#b0b4c0] hover:text-[#f0f0f5]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Back to Gallery
        </Link>
      </div>
    </div>
  );

  if (mode === 'error' || !collection) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="text-center">
        <p className="text-[#6b7084] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Collection not found</p>
        <Link to="/gallery" className="text-xs tracking-[0.2em] uppercase text-[#b0b4c0] hover:text-[#f0f0f5]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Back to Gallery
        </Link>
      </div>
    </div>
  );

  const photos = collection.photos || [];

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="curated-gallery">
      <header className="px-6 md:px-12 pt-8 pb-6">
        <div className="max-w-[1000px] mx-auto">
          <div className="mb-6">
            <Breadcrumb>
              <BreadcrumbList style={{ gap: 0 }}>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/gallery" style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Logotype app="framed" size="sidebar" />
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator style={{ padding: '0 8px', color: '#454860' }} />
                <BreadcrumbItem>
                  <BreadcrumbPage style={{ color: '#f0f0f5', fontSize: 13, fontWeight: 400 }}>
                    {collection.title}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2"
            style={{ fontFamily: "'Playfair Display', serif", color: '#f0f0f5', lineHeight: 1 }}>
            {collection.title}
          </h1>
          {collection.description && (
            <p className="text-sm italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b0b4c0' }}>
              {collection.description}
            </p>
          )}
          <p className="text-xs mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#6b7084' }}>
            {photos.length} {photos.length === 1 ? 'photograph' : 'photographs'}
          </p>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 md:px-8 pb-20">
        {photos.length === 0 ? (
          <ZEmptyState mark="F" accent="#D4537E" message="This collection is empty." />
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-3">
            {photos.map((photo, i) => {
              const tagline = photo.tagline || photo.title || '';
              return (
                <div key={photo.id} className="mb-3 break-inside-avoid group cursor-pointer" onClick={() => setViewerIndex(i)}>
                  <img src={getImageUrl(photo.url)} alt="" loading="lazy"
                    className="w-full object-cover rounded-sm transition-all duration-700 group-hover:brightness-110" />
                  {tagline && (
                    <p className="text-xs italic mt-1.5 text-[#b0b4c0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {tagline}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-center py-12">
        <p className="text-xs tracking-[0.3em]" style={{ fontFamily: "'Cormorant Garamond', serif", color: 'rgba(255,255,255,0.06)' }}>
          WERNER LABS
        </p>
      </div>

      {viewerIndex >= 0 && (
        <Viewer photos={photos} index={viewerIndex} onClose={() => setViewerIndex(-1)} onNavigate={onNavigate} />
      )}
    </div>
  );
}
